import type { SupportedLocale } from '@erp/shared';

export const centralMailTranslations = {
  de: {
    settingsTitle: 'E-Mail',
    workspaceTitle: 'Geschäftliche E-Mails',
    accounts: {
      title: 'E-Mail-Konten',
      description: 'Persönliche und gemeinsame Postfächer, Aliase und Zugriffsrechte verwalten.',
      scope: 'Diese Konten und Berechtigungen gelten nur für den aktuellen Mandanten.',
      add: 'E-Mail-Konto hinzufügen',
      edit: 'E-Mail-Konto bearbeiten',
      form: 'E-Mail-Konto',
      displayName: 'Anzeigename',
      primaryAddress: 'Hauptadresse',
      syncFrom: 'Nachrichten übernehmen ab',
      type: 'Kontoart',
      sharing: 'Freigabe in Akten',
      active: 'Konto für Abrufe und Versand aktivieren',
      personalHint:
        'Persönliche Postfächer geben Nachrichten nur einzeln nach ausdrücklicher Freigabe für gemeinsame Akten frei. Verwaltung allein erlaubt keinen Einblick.',
      sharedHint:
        'Auch gemeinsame Postfächer benötigen ausdrückliche Leserechte und eine ausgewählte Aktenfreigabe.',
      allFolders:
        'Der Importumfang umfasst alle technisch zugänglichen Ordner ab dem gewählten Datum, einschließlich Archiv, Unterordnern, Spam und Papierkorb.',
      advanced: 'Erweitert: Versandverbindung und Aliase',
      saved: 'E-Mail-Konto gespeichert',
      empty: 'Noch keine E-Mail-Konten eingerichtet.',
      emptyHint:
        'Legen Sie das erste geschäftliche Postfach mit seinen berechtigten Mitarbeitern an.',
      noResults: 'Keine passenden Postfächer.',
      status: 'Synchronisierung',
      never: 'Noch kein Abruf',
      lastSuccess: 'Letzter vollständiger Abruf',
      responsible: 'Zuständig',
      search: 'Postfächer suchen',
      sort: 'Sortierung',
      nameAsc: 'Name aufsteigend',
      nameDesc: 'Name absteigend',
      updatedDesc: 'Zuletzt geändert',
      help: 'Hilfe zur Postfachverwaltung',
      disabled: 'Deaktiviert',
      enabled: 'Aktiv',
      editAction: 'Verwalten',
    },
    types: {
      PERSONAL: 'Persönliches geschäftliches Postfach',
      SHARED: 'Gemeinsames Funktionspostfach',
    },
    sharing: {
      PRIVATE: 'Keine Freigabe in gemeinsamen Akten',
      MANUAL: 'Jede Nachricht ausdrücklich freigeben',
      AUTO_BUSINESS: 'Eindeutige Geschäftspartner-Mails automatisch freigeben',
    },
    incoming: {
      title: 'Empfang über IMAP',
      host: 'IMAP-Server',
      username: 'IMAP-Benutzer',
      secret: 'Kennwort oder Zugriffstoken für Empfang',
      clearSecret: 'Gespeicherte Zugangsdaten für Empfang entfernen',
    },
    outgoing: {
      title: 'Versand über SMTP',
      host: 'SMTP-Server',
      username: 'SMTP-Benutzer',
      secret: 'Kennwort oder Zugriffstoken für Versand',
      clearSecret: 'Gespeicherte Zugangsdaten für Versand entfernen',
      mode: 'Versandverbindung',
      tenant: 'Bestehenden Versand dieses Mandanten verwenden',
      custom: 'Eigene SMTP-Verbindung für dieses Postfach',
      tenantHint:
        'Der bestehende Belegversand und die mandantenweite Versandsperre gelten weiter. Ein Konto oder Alias erteilt noch kein Versandrecht.',
    },
    connection: {
      changed: 'Für die geänderte Verbindung sind neue Zugangsdaten erforderlich.',
      port: 'Port',
      security: 'Verbindungssicherheit',
      tls: 'Direktes TLS',
      starttls: 'STARTTLS erforderlich',
      authentication: 'Anmeldung',
      password: 'Kennwort oder App-Kennwort',
      oauth: 'OAuth2-Zugriffstoken',
      stored: 'Zugangsdaten sind gespeichert. Leer lassen, um sie beizubehalten.',
      secretHint: 'Wird verschlüsselt gespeichert und danach nicht mehr angezeigt.',
      expires: 'Token gültig bis',
    },
    members: {
      title: 'Mitarbeiter und Rechte',
      hint: 'Zuständigkeit, Lesen, Bearbeiten, Versand, Verwaltung und BCC sind getrennt. Eine neue Zuordnung vergibt keine Rechte automatisch. Zusätzlich gelten die Rollenrechte.',
      employee: 'Mitarbeiter',
      choose: 'Mitarbeiter auswählen',
      add: 'Mitarbeiter hinzufügen',
      unavailable: 'Mitarbeiter nicht mehr verfügbar',
      permissionFor: '{{permission}}: {{name}}',
      isResponsible: 'Zuständig',
      canRead: 'Lesen',
      canEdit: 'Bearbeiten',
      canSend: 'Versenden',
      canAdmin: 'Verwalten',
      canReadBcc: 'BCC lesen',
    },
    aliases: {
      title: 'Aliase und zusätzliche Absender',
      hint: 'Aliase gehören zum selben Postfach und werden nicht separat synchronisiert. Die Adresse muss beim Mailanbieter für dieses Postfach verfügbar sein.',
      address: 'Aliasadresse',
      canSend: 'Als Absender zulassen',
      add: 'Alias hinzufügen',
    },
    errors: {
      invalidAccount: 'Die Kontokonfiguration ist unvollständig. Bitte prüfen Sie das Konto.',
      versionConflict: 'Das Konto wurde zwischenzeitlich geändert. Bitte laden Sie es neu.',
      addressConflict:
        'Diese Adresse gehört bereits zu einem Postfach dieses Mandanten. Prüfen Sie Hauptadresse und Aliase.',
      saveFailed: 'Das Konto konnte nicht gespeichert werden. Ihre Eingaben bleiben erhalten.',
      loadFailed: 'Die Postfächer konnten nicht geladen werden.',
      employeesFailed: 'Die Mitarbeiter konnten nicht geladen werden.',
    },
    validation: {
      typeImmutable:
        'Die Kontoart wird beim Anlegen festgelegt und kann zum Schutz persönlicher Nachrichten nicht nachträglich geändert werden.',
      checkFields: 'Bitte prüfen Sie die markierten Felder.',
      invalid: 'Bitte einen gültigen Wert eingeben.',
      secretConflict: 'Zugangsdaten entweder ersetzen oder entfernen.',
      responsibleRequired: 'Mindestens ein zuständiger Mitarbeiter ist erforderlich.',
      personalSharing:
        'Persönliche Postfächer benötigen eine ausdrückliche Freigabe jeder Nachricht.',
      duplicateMember: 'Dieser Mitarbeiter ist bereits zugeordnet.',
      duplicateAddress: 'Diese Adresse ist im Konto mehrfach eingetragen.',
      credentialsRequired: 'Für ein aktives Konto sind passende Zugangsdaten erforderlich.',
      keepAdministration: 'Behalten Sie Ihre Verwaltungsberechtigung für dieses Konto bei.',
      employeeUnavailable: 'Wählen Sie ausschließlich aktive Mitarbeiter dieses Mandanten.',
    },
    statuses: {
      NOT_STARTED: 'Noch nicht gestartet',
      QUEUED: 'Geplant',
      RUNNING: 'Wird abgerufen',
      PARTIAL: 'Teilweise abgerufen',
      SUCCEEDED: 'Vollständig geprüft',
      FAILED: 'Abruf fehlgeschlagen',
      PAUSED: 'Abruf pausiert',
    },
  },
  en: {
    settingsTitle: 'Email',
    workspaceTitle: 'Business emails',
    accounts: {
      title: 'Email accounts',
      description: 'Manage personal and shared mailboxes, aliases and access rights.',
      scope: 'These accounts and permissions apply only to the current tenant.',
      add: 'Add email account',
      edit: 'Edit email account',
      form: 'Email account',
      displayName: 'Display name',
      primaryAddress: 'Primary address',
      syncFrom: 'Import messages from',
      type: 'Account type',
      sharing: 'Sharing in records',
      active: 'Activate account for sync and sending',
      personalHint:
        'Personal mailboxes release individual messages to shared records only after explicit approval. Administration alone does not allow reading.',
      sharedHint:
        'Shared mailboxes also require explicit read grants and a selected record-sharing policy.',
      allFolders:
        'Import covers every technically accessible folder from the selected date, including archives, subfolders, spam and trash.',
      advanced: 'Advanced: outgoing connection and aliases',
      saved: 'Email account saved',
      empty: 'No email accounts configured yet.',
      emptyHint: 'Add the first business mailbox and its authorized employees.',
      noResults: 'No matching mailboxes.',
      status: 'Synchronization',
      never: 'No sync yet',
      lastSuccess: 'Last complete sync',
      responsible: 'Responsible',
      search: 'Search mailboxes',
      sort: 'Sort order',
      nameAsc: 'Name ascending',
      nameDesc: 'Name descending',
      updatedDesc: 'Recently changed',
      help: 'Mailbox management help',
      disabled: 'Inactive',
      enabled: 'Active',
      editAction: 'Manage',
    },
    types: {
      PERSONAL: 'Personal business mailbox',
      SHARED: 'Shared business mailbox',
    },
    sharing: {
      PRIVATE: 'No sharing in common records',
      MANUAL: 'Explicitly release each message',
      AUTO_BUSINESS: 'Automatically release unambiguous business partner mail',
    },
    incoming: {
      title: 'Receive via IMAP',
      host: 'IMAP server',
      username: 'IMAP username',
      secret: 'Password or access token for receiving',
      clearSecret: 'Remove stored receiving credentials',
    },
    outgoing: {
      title: 'Send via SMTP',
      host: 'SMTP server',
      username: 'SMTP username',
      secret: 'Password or access token for sending',
      clearSecret: 'Remove stored sending credentials',
      mode: 'Outgoing connection',
      tenant: 'Use this tenant’s existing delivery settings',
      custom: 'Dedicated SMTP connection for this mailbox',
      tenantHint:
        'Existing document delivery and the tenant-wide sending switch still apply. An account or alias alone does not grant sending rights.',
    },
    connection: {
      changed: 'The changed connection requires new credentials.',
      port: 'Port',
      security: 'Connection security',
      tls: 'Direct TLS',
      starttls: 'Require STARTTLS',
      authentication: 'Authentication',
      password: 'Password or app password',
      oauth: 'OAuth2 access token',
      stored: 'Credentials are stored. Leave empty to retain them.',
      secretHint: 'Stored encrypted and never displayed again.',
      expires: 'Token valid until',
    },
    members: {
      title: 'Employees and permissions',
      hint: 'Responsibility, reading, editing, sending, administration and BCC are separate. Adding a member grants no rights automatically. Role permissions also apply.',
      employee: 'Employee',
      choose: 'Select employee',
      add: 'Add employee',
      unavailable: 'Employee no longer available',
      permissionFor: '{{permission}}: {{name}}',
      isResponsible: 'Responsible',
      canRead: 'Read',
      canEdit: 'Edit',
      canSend: 'Send',
      canAdmin: 'Manage',
      canReadBcc: 'Read BCC',
    },
    aliases: {
      title: 'Aliases and additional senders',
      hint: 'Aliases belong to the same mailbox and have no separate synchronization. The provider must make the address available for this mailbox.',
      address: 'Alias address',
      canSend: 'Allow as sender',
      add: 'Add alias',
    },
    errors: {
      invalidAccount: 'The account configuration is incomplete. Please review it.',
      versionConflict: 'The account has changed. Please reload it.',
      addressConflict:
        'This address already belongs to a mailbox in this tenant. Check the primary address and aliases.',
      saveFailed: 'The account could not be saved. Your inputs are retained.',
      loadFailed: 'Mailboxes could not be loaded.',
      employeesFailed: 'Employees could not be loaded.',
    },
    validation: {
      typeImmutable:
        'The account type is fixed when created and cannot later be changed, protecting personal messages.',
      checkFields: 'Please check the highlighted fields.',
      invalid: 'Enter a valid value.',
      secretConflict: 'Either replace or remove credentials.',
      responsibleRequired: 'At least one responsible employee is required.',
      personalSharing: 'Personal mailboxes require explicit release of each message.',
      duplicateMember: 'This employee is already assigned.',
      duplicateAddress: 'This address appears more than once in the account.',
      credentialsRequired: 'An active account requires matching credentials.',
      keepAdministration: 'Retain your administration rights for this account.',
      employeeUnavailable: 'Select only active employees of this tenant.',
    },
    statuses: {
      NOT_STARTED: 'Not started',
      QUEUED: 'Queued',
      RUNNING: 'Synchronizing',
      PARTIAL: 'Partially synchronized',
      SUCCEEDED: 'Completely checked',
      FAILED: 'Sync failed',
      PAUSED: 'Sync paused',
    },
  },
  fr: {
    settingsTitle: 'E-mail',
    workspaceTitle: 'E-mails professionnels',
    accounts: {
      title: 'Comptes e-mail',
      description: 'Gérer les boîtes personnelles et partagées, les alias et les droits d’accès.',
      scope: 'Ces comptes et droits concernent uniquement l’entreprise actuelle.',
      add: 'Ajouter un compte e-mail',
      edit: 'Modifier le compte e-mail',
      form: 'Compte e-mail',
      displayName: 'Nom affiché',
      primaryAddress: 'Adresse principale',
      syncFrom: 'Importer les messages à partir du',
      type: 'Type de compte',
      sharing: 'Partage dans les dossiers',
      active: 'Activer le compte pour la synchronisation et l’envoi',
      personalHint:
        'Les boîtes personnelles partagent chaque message dans les dossiers communs uniquement après autorisation explicite. L’administration seule ne permet pas la lecture.',
      sharedHint:
        'Les boîtes partagées nécessitent aussi des droits de lecture explicites et une règle de partage.',
      allFolders:
        'L’import comprend tous les dossiers techniquement accessibles depuis la date choisie, y compris archives, sous-dossiers, indésirables et corbeille.',
      advanced: 'Avancé : connexion d’envoi et alias',
      saved: 'Compte e-mail enregistré',
      empty: 'Aucun compte e-mail configuré.',
      emptyHint: 'Ajoutez la première boîte professionnelle et ses collaborateurs autorisés.',
      noResults: 'Aucune boîte correspondante.',
      status: 'Synchronisation',
      never: 'Aucune synchronisation',
      lastSuccess: 'Dernière synchronisation complète',
      responsible: 'Responsable',
      search: 'Rechercher des boîtes',
      sort: 'Tri',
      nameAsc: 'Nom croissant',
      nameDesc: 'Nom décroissant',
      updatedDesc: 'Modifications récentes',
      help: 'Aide à la gestion des boîtes',
      disabled: 'Inactif',
      enabled: 'Actif',
      editAction: 'Gérer',
    },
    types: {
      PERSONAL: 'Boîte professionnelle personnelle',
      SHARED: 'Boîte professionnelle partagée',
    },
    sharing: {
      PRIVATE: 'Aucun partage dans les dossiers communs',
      MANUAL: 'Autoriser explicitement chaque message',
      AUTO_BUSINESS: 'Partager automatiquement les e-mails partenaires non ambigus',
    },
    incoming: {
      title: 'Réception par IMAP',
      host: 'Serveur IMAP',
      username: 'Identifiant IMAP',
      secret: 'Mot de passe ou jeton d’accès pour la réception',
      clearSecret: 'Supprimer les identifiants de réception enregistrés',
    },
    outgoing: {
      title: 'Envoi par SMTP',
      host: 'Serveur SMTP',
      username: 'Identifiant SMTP',
      secret: 'Mot de passe ou jeton d’accès pour l’envoi',
      clearSecret: 'Supprimer les identifiants d’envoi enregistrés',
      mode: 'Connexion d’envoi',
      tenant: 'Utiliser l’envoi existant de cette entreprise',
      custom: 'Connexion SMTP dédiée à cette boîte',
      tenantHint:
        'L’envoi de documents existant et son interrupteur général restent applicables. Un compte ou alias n’accorde aucun droit d’envoi à lui seul.',
    },
    connection: {
      changed: 'La connexion modifiée nécessite de nouveaux identifiants.',
      port: 'Port',
      security: 'Sécurité de la connexion',
      tls: 'TLS direct',
      starttls: 'STARTTLS obligatoire',
      authentication: 'Authentification',
      password: 'Mot de passe ou mot de passe d’application',
      oauth: 'Jeton d’accès OAuth2',
      stored: 'Les identifiants sont enregistrés. Laissez vide pour les conserver.',
      secretHint: 'Enregistré sous forme chiffrée et jamais réaffiché.',
      expires: 'Jeton valable jusqu’au',
    },
    members: {
      title: 'Collaborateurs et droits',
      hint: 'Responsabilité, lecture, modification, envoi, administration et CCI sont séparés. Un nouvel ajout n’accorde aucun droit automatiquement. Les droits du rôle s’appliquent aussi.',
      employee: 'Collaborateur',
      choose: 'Choisir un collaborateur',
      add: 'Ajouter un collaborateur',
      unavailable: 'Collaborateur indisponible',
      permissionFor: '{{permission}} : {{name}}',
      isResponsible: 'Responsable',
      canRead: 'Lire',
      canEdit: 'Modifier',
      canSend: 'Envoyer',
      canAdmin: 'Administrer',
      canReadBcc: 'Lire les CCI',
    },
    aliases: {
      title: 'Alias et expéditeurs supplémentaires',
      hint: 'Les alias appartiennent à la même boîte et ne sont pas synchronisés séparément. Le fournisseur doit autoriser cette adresse pour la boîte.',
      address: 'Adresse d’alias',
      canSend: 'Autoriser comme expéditeur',
      add: 'Ajouter un alias',
    },
    errors: {
      invalidAccount: 'La configuration est incomplète. Veuillez vérifier le compte.',
      versionConflict: 'Le compte a été modifié entre-temps. Rechargez-le.',
      addressConflict:
        'Cette adresse appartient déjà à une boîte de cette entreprise. Vérifiez l’adresse principale et les alias.',
      saveFailed: 'Le compte n’a pas pu être enregistré. Vos saisies sont conservées.',
      loadFailed: 'Impossible de charger les boîtes.',
      employeesFailed: 'Impossible de charger les collaborateurs.',
    },
    validation: {
      typeImmutable:
        'Le type est fixé à la création et ne peut plus être modifié afin de protéger les messages personnels.',
      checkFields: 'Veuillez vérifier les champs signalés.',
      invalid: 'Saisissez une valeur valide.',
      secretConflict: 'Remplacez ou supprimez les identifiants, mais pas les deux.',
      responsibleRequired: 'Au moins un collaborateur responsable est requis.',
      personalSharing:
        'Les boîtes personnelles exigent l’autorisation explicite de chaque message.',
      duplicateMember: 'Ce collaborateur est déjà affecté.',
      duplicateAddress: 'Cette adresse est présente plusieurs fois dans le compte.',
      credentialsRequired: 'Un compte actif nécessite des identifiants adaptés.',
      keepAdministration: 'Conservez votre droit d’administration de ce compte.',
      employeeUnavailable: 'Choisissez uniquement des collaborateurs actifs de cette entreprise.',
    },
    statuses: {
      NOT_STARTED: 'Non démarrée',
      QUEUED: 'Planifiée',
      RUNNING: 'Synchronisation en cours',
      PARTIAL: 'Synchronisation partielle',
      SUCCEEDED: 'Vérification complète',
      FAILED: 'Échec de synchronisation',
      PAUSED: 'Synchronisation suspendue',
    },
  },
  es: {
    settingsTitle: 'Correo electrónico',
    workspaceTitle: 'Correos profesionales',
    accounts: {
      title: 'Cuentas de correo',
      description: 'Gestionar buzones personales y compartidos, alias y permisos de acceso.',
      scope: 'Estas cuentas y permisos solo se aplican a la empresa actual.',
      add: 'Añadir cuenta de correo',
      edit: 'Editar cuenta de correo',
      form: 'Cuenta de correo',
      displayName: 'Nombre visible',
      primaryAddress: 'Dirección principal',
      syncFrom: 'Importar mensajes desde',
      type: 'Tipo de cuenta',
      sharing: 'Compartir en fichas',
      active: 'Activar la cuenta para sincronización y envío',
      personalHint:
        'Los buzones personales comparten cada mensaje en fichas comunes solo tras una autorización expresa. Administrar no permite leer su contenido.',
      sharedHint:
        'Los buzones compartidos también requieren permisos explícitos de lectura y una regla para compartir.',
      allFolders:
        'La importación incluye todas las carpetas técnicamente accesibles desde la fecha elegida, incluidos archivos, subcarpetas, spam y papelera.',
      advanced: 'Avanzado: conexión de envío y alias',
      saved: 'Cuenta de correo guardada',
      empty: 'Todavía no hay cuentas configuradas.',
      emptyHint: 'Añada el primer buzón profesional y sus empleados autorizados.',
      noResults: 'No hay buzones coincidentes.',
      status: 'Sincronización',
      never: 'Sin sincronizaciones',
      lastSuccess: 'Última sincronización completa',
      responsible: 'Responsable',
      search: 'Buscar buzones',
      sort: 'Orden',
      nameAsc: 'Nombre ascendente',
      nameDesc: 'Nombre descendente',
      updatedDesc: 'Cambios recientes',
      help: 'Ayuda de gestión de buzones',
      disabled: 'Inactiva',
      enabled: 'Activa',
      editAction: 'Gestionar',
    },
    types: {
      PERSONAL: 'Buzón profesional personal',
      SHARED: 'Buzón profesional compartido',
    },
    sharing: {
      PRIVATE: 'No compartir en fichas comunes',
      MANUAL: 'Autorizar expresamente cada mensaje',
      AUTO_BUSINESS: 'Compartir automáticamente correos de socios sin ambigüedad',
    },
    incoming: {
      title: 'Recepción por IMAP',
      host: 'Servidor IMAP',
      username: 'Usuario IMAP',
      secret: 'Contraseña o token de acceso para recepción',
      clearSecret: 'Eliminar las credenciales de recepción guardadas',
    },
    outgoing: {
      title: 'Envío por SMTP',
      host: 'Servidor SMTP',
      username: 'Usuario SMTP',
      secret: 'Contraseña o token de acceso para envío',
      clearSecret: 'Eliminar las credenciales de envío guardadas',
      mode: 'Conexión de envío',
      tenant: 'Usar el envío existente de esta empresa',
      custom: 'Conexión SMTP propia para este buzón',
      tenantHint:
        'Se mantienen el envío de documentos existente y el interruptor general de envío. Una cuenta o alias no concede permiso para enviar por sí solo.',
    },
    connection: {
      changed: 'La conexión modificada requiere nuevas credenciales.',
      port: 'Puerto',
      security: 'Seguridad de conexión',
      tls: 'TLS directo',
      starttls: 'Exigir STARTTLS',
      authentication: 'Autenticación',
      password: 'Contraseña o contraseña de aplicación',
      oauth: 'Token de acceso OAuth2',
      stored: 'Las credenciales están guardadas. Deje vacío para conservarlas.',
      secretHint: 'Se guarda cifrado y nunca vuelve a mostrarse.',
      expires: 'Token válido hasta',
    },
    members: {
      title: 'Empleados y permisos',
      hint: 'Responsabilidad, lectura, edición, envío, administración y CCO son independientes. Añadir una persona no concede permisos automáticamente. También se aplican los permisos de su rol.',
      employee: 'Empleado',
      choose: 'Elegir empleado',
      add: 'Añadir empleado',
      unavailable: 'Empleado no disponible',
      permissionFor: '{{permission}}: {{name}}',
      isResponsible: 'Responsable',
      canRead: 'Leer',
      canEdit: 'Editar',
      canSend: 'Enviar',
      canAdmin: 'Administrar',
      canReadBcc: 'Leer CCO',
    },
    aliases: {
      title: 'Alias y remitentes adicionales',
      hint: 'Los alias pertenecen al mismo buzón y no se sincronizan por separado. El proveedor debe habilitar la dirección para ese buzón.',
      address: 'Dirección del alias',
      canSend: 'Permitir como remitente',
      add: 'Añadir alias',
    },
    errors: {
      invalidAccount: 'La configuración está incompleta. Revise la cuenta.',
      versionConflict: 'La cuenta ha cambiado. Vuelva a cargarla.',
      addressConflict:
        'Esta dirección ya pertenece a un buzón de esta empresa. Revise la dirección principal y los alias.',
      saveFailed: 'No se pudo guardar la cuenta. Se conservan los datos introducidos.',
      loadFailed: 'No se pudieron cargar los buzones.',
      employeesFailed: 'No se pudieron cargar los empleados.',
    },
    validation: {
      typeImmutable:
        'El tipo se fija al crear la cuenta y no puede cambiarse después, para proteger los mensajes personales.',
      checkFields: 'Revise los campos marcados.',
      invalid: 'Introduzca un valor válido.',
      secretConflict: 'Sustituya o elimine las credenciales, no ambas cosas.',
      responsibleRequired: 'Se necesita al menos un empleado responsable.',
      personalSharing: 'Los buzones personales requieren autorización expresa para cada mensaje.',
      duplicateMember: 'Este empleado ya está asignado.',
      duplicateAddress: 'Esta dirección aparece varias veces en la cuenta.',
      credentialsRequired: 'Una cuenta activa requiere credenciales adecuadas.',
      keepAdministration: 'Conserve su permiso de administración para esta cuenta.',
      employeeUnavailable: 'Seleccione solo empleados activos de esta empresa.',
    },
    statuses: {
      NOT_STARTED: 'Sin iniciar',
      QUEUED: 'En cola',
      RUNNING: 'Sincronizando',
      PARTIAL: 'Sincronización parcial',
      SUCCEEDED: 'Comprobación completa',
      FAILED: 'Error de sincronización',
      PAUSED: 'Sincronización pausada',
    },
  },
  tr: {
    settingsTitle: 'E-posta',
    workspaceTitle: 'İş e-postaları',
    accounts: {
      title: 'E-posta hesapları',
      description:
        'Kişisel ve ortak posta kutularını, takma adresleri ve erişim yetkilerini yönetin.',
      scope: 'Bu hesaplar ve yetkiler yalnızca mevcut kiracı için geçerlidir.',
      add: 'E-posta hesabı ekle',
      edit: 'E-posta hesabını düzenle',
      form: 'E-posta hesabı',
      displayName: 'Görünen ad',
      primaryAddress: 'Ana adres',
      syncFrom: 'Bu tarihten itibaren iletileri aktar',
      type: 'Hesap türü',
      sharing: 'Kayıtlarda paylaşım',
      active: 'Hesabı senkronizasyon ve gönderim için etkinleştir',
      personalHint:
        'Kişisel posta kutuları, her ileti açıkça onaylandıktan sonra ortak kayıtlarda paylaşılır. Yalnızca yönetim yetkisi içerikleri okumaya izin vermez.',
      sharedHint:
        'Ortak posta kutuları için de açık okuma yetkisi ve kayıt paylaşım kuralı gerekir.',
      allFolders:
        'İçe aktarım, seçilen tarihten itibaren arşivler, alt klasörler, istenmeyen posta ve çöp kutusu dahil teknik olarak erişilebilen tüm klasörleri kapsar.',
      advanced: 'Gelişmiş: gönderim bağlantısı ve takma adresler',
      saved: 'E-posta hesabı kaydedildi',
      empty: 'Henüz e-posta hesabı yapılandırılmadı.',
      emptyHint: 'İlk iş posta kutusunu ve yetkili çalışanlarını ekleyin.',
      noResults: 'Eşleşen posta kutusu yok.',
      status: 'Senkronizasyon',
      never: 'Henüz senkronize edilmedi',
      lastSuccess: 'Son tam senkronizasyon',
      responsible: 'Sorumlu',
      search: 'Posta kutularını ara',
      sort: 'Sıralama',
      nameAsc: 'Ada göre artan',
      nameDesc: 'Ada göre azalan',
      updatedDesc: 'Son değiştirilenler',
      help: 'Posta kutusu yönetimi yardımı',
      disabled: 'Devre dışı',
      enabled: 'Etkin',
      editAction: 'Yönet',
    },
    types: {
      PERSONAL: 'Kişisel iş posta kutusu',
      SHARED: 'Ortak iş posta kutusu',
    },
    sharing: {
      PRIVATE: 'Ortak kayıtlarda paylaşma',
      MANUAL: 'Her iletiyi açıkça onayla',
      AUTO_BUSINESS: 'İş ortağı net olarak belirlenen iletileri otomatik paylaş',
    },
    incoming: {
      title: 'IMAP ile alım',
      host: 'IMAP sunucusu',
      username: 'IMAP kullanıcı adı',
      secret: 'Alım için parola veya erişim belirteci',
      clearSecret: 'Kayıtlı alım kimlik bilgilerini kaldır',
    },
    outgoing: {
      title: 'SMTP ile gönderim',
      host: 'SMTP sunucusu',
      username: 'SMTP kullanıcı adı',
      secret: 'Gönderim için parola veya erişim belirteci',
      clearSecret: 'Kayıtlı gönderim kimlik bilgilerini kaldır',
      mode: 'Gönderim bağlantısı',
      tenant: 'Bu kiracının mevcut gönderim ayarlarını kullan',
      custom: 'Bu posta kutusuna özel SMTP bağlantısı',
      tenantHint:
        'Mevcut belge gönderimi ve kiracı genelindeki gönderim anahtarı geçerliliğini korur. Bir hesap veya takma adres tek başına gönderim yetkisi vermez.',
    },
    connection: {
      changed: 'Değiştirilen bağlantı için yeni kimlik bilgileri gereklidir.',
      port: 'Bağlantı noktası',
      security: 'Bağlantı güvenliği',
      tls: 'Doğrudan TLS',
      starttls: 'STARTTLS zorunlu',
      authentication: 'Kimlik doğrulama',
      password: 'Parola veya uygulama parolası',
      oauth: 'OAuth2 erişim belirteci',
      stored: 'Kimlik bilgileri kayıtlı. Korumak için boş bırakın.',
      secretHint: 'Şifreli saklanır ve daha sonra görüntülenmez.',
      expires: 'Belirtecin geçerlilik sonu',
    },
    members: {
      title: 'Çalışanlar ve yetkiler',
      hint: 'Sorumluluk, okuma, düzenleme, gönderim, yönetim ve BCC ayrıdır. Yeni çalışan eklemek otomatik yetki vermez. Rol yetkileri de geçerlidir.',
      employee: 'Çalışan',
      choose: 'Çalışan seçin',
      add: 'Çalışan ekle',
      unavailable: 'Çalışan artık kullanılabilir değil',
      permissionFor: '{{permission}}: {{name}}',
      isResponsible: 'Sorumlu',
      canRead: 'Oku',
      canEdit: 'Düzenle',
      canSend: 'Gönder',
      canAdmin: 'Yönet',
      canReadBcc: 'BCC oku',
    },
    aliases: {
      title: 'Takma adresler ve ek gönderenler',
      hint: 'Takma adresler aynı posta kutusuna aittir ve ayrı senkronize edilmez. Sağlayıcı bu adresi posta kutusu için kullanılabilir kılmalıdır.',
      address: 'Takma adres',
      canSend: 'Gönderen olarak izin ver',
      add: 'Takma adres ekle',
    },
    errors: {
      invalidAccount: 'Hesap yapılandırması eksik. Lütfen hesabı kontrol edin.',
      versionConflict: 'Hesap bu sırada değiştirildi. Yeniden yükleyin.',
      addressConflict:
        'Bu adres zaten bu kiracının bir posta kutusuna ait. Ana adresi ve takma adresleri kontrol edin.',
      saveFailed: 'Hesap kaydedilemedi. Girdileriniz korunuyor.',
      loadFailed: 'Posta kutuları yüklenemedi.',
      employeesFailed: 'Çalışanlar yüklenemedi.',
    },
    validation: {
      typeImmutable:
        'Hesap türü oluşturulurken belirlenir ve kişisel iletileri korumak için sonradan değiştirilemez.',
      checkFields: 'İşaretli alanları kontrol edin.',
      invalid: 'Geçerli bir değer girin.',
      secretConflict: 'Kimlik bilgilerini değiştirin veya kaldırın; ikisini birlikte yapmayın.',
      responsibleRequired: 'En az bir sorumlu çalışan gereklidir.',
      personalSharing: 'Kişisel posta kutuları her ileti için açık paylaşım onayı gerektirir.',
      duplicateMember: 'Bu çalışan zaten atanmış.',
      duplicateAddress: 'Bu adres hesapta birden fazla kez bulunuyor.',
      credentialsRequired: 'Etkin hesap için uygun kimlik bilgileri gerekir.',
      keepAdministration: 'Bu hesap için yönetim yetkinizi koruyun.',
      employeeUnavailable: 'Yalnızca bu kiracının etkin çalışanlarını seçin.',
    },
    statuses: {
      NOT_STARTED: 'Başlatılmadı',
      QUEUED: 'Sırada',
      RUNNING: 'Senkronize ediliyor',
      PARTIAL: 'Kısmen senkronize',
      SUCCEEDED: 'Tam kontrol edildi',
      FAILED: 'Senkronizasyon başarısız',
      PAUSED: 'Senkronizasyon duraklatıldı',
    },
  },
} as const satisfies Record<SupportedLocale, unknown>;
